import { useInternalRouter, useInternalRouterUtils } from '@/common/stores/internalRouter'
import { useNavigate } from 'react-router-dom'

interface UseGoBackCallbackProps {
  fallback?: string
  searchLastRouteFromFallback?: boolean
  searchLastRouteIgnoringFilters?: boolean
  removeFilters?: boolean
}

/**
 *
 * @param searchLastRouteFromFallback
 * ex:
 * current route -> /users/123/actions
 * fallback -> "/users"
 * return (navigate to) => /users?search=123
 *
 *
 * @param searchLastRouteIgnoringFilters
 *  * ex:
 * current route -> /establishment-bills/456
 * previous route -> /establishments/123/bills?search=456
 * return (navigate to) =>  /establishments/123/bills?search=456
 *
 *  * ex:
 * current route -> /establishment-bills/456
 * previous route -> /establishment-bills?search=456
 * return (navigate to) => /establishment-bills?search=456
 *
 *  * ex:
 * current route -> /users/123/actions
 * previous route -> /users/123
 * return (navigate to) => /users/123
 *
 */

export const useGoBack = () => {
  const { paths, removeLastPath, removePathsFromIndex } = useInternalRouter()
  const { getRouteIndex, getPreviousRoute, getPreviousRouteFromPathname, getPreviousRouteWithoutFilters, getPath } =
    useInternalRouterUtils()

  const navigate = useNavigate()

  const thereIsAPrevPage = paths.length > 1

  if (thereIsAPrevPage) {
    return ({
      fallback,
      searchLastRouteFromFallback,
      searchLastRouteIgnoringFilters,
      removeFilters,
    }: UseGoBackCallbackProps) => {
      const previousRouteWithoutFilters = getPreviousRouteWithoutFilters()

      if (searchLastRouteIgnoringFilters && previousRouteWithoutFilters) {
        removePathsFromIndex(getRouteIndex(previousRouteWithoutFilters) + 1)
        return navigate(getPath(previousRouteWithoutFilters, removeFilters))
      }

      if (searchLastRouteFromFallback && fallback) {
        const previousRouteFromFallback = getPreviousRouteFromPathname(fallback)

        if (previousRouteFromFallback) {
          removePathsFromIndex(getRouteIndex(previousRouteFromFallback) + 1)
          return navigate(getPath(previousRouteFromFallback, removeFilters))
        }

        return navigate(fallback || '/')
      } else {
        const previousRoute = getPreviousRoute()

        if (previousRoute) {
          navigate(getPath(previousRoute, removeFilters))
          removeLastPath()
        } else {
          navigate(fallback || '/')
        }
      }
    }
  }

  return ({ fallback }: UseGoBackCallbackProps) => navigate(fallback || '/')
}
